// ===== 詳細 =====

import { useState } from "react";
import { Stack, Typography, Paper, TextField, Alert } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import StyledButton from "../../../components/StyledButton";
import QrListDialog from "../../../components/dialog/QrListDialog";
import { dataEntityText } from "../../../components/links/EntityLinks";
import { issuanceRow } from "../../../schema/console/issuance";
import { materialSearchUrl } from "../../../util/Materialperser";
import { userInfoCol } from "../../../util/userDisplay";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { materialIdRange } from "./helpers";

export function IssuanceDetailPage({ pageContext }: { pageContext: PageContext }) {
  const { id } = useParams<{ id: string }>();
  const api = pageContext.api;
  const navigate = useNavigate();
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<issuanceRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.issuance.getFromId({ id });
  }, [api, id, retryCount]);

  return (
    <DetailGuard state={state} notFoundLabel={`発行 ${id}`} onRetry={retry}>
      {(info) => <IssuanceInfoView info={info} onEdit={() => navigate("edit/")} />}
    </DetailGuard>
  );
}

function IssuanceInfoView({ info, onEdit }: { info: issuanceRow; onEdit: () => void }) {
  const [showMaterialQr, setShowMaterialQr] = useState(false);
  const [showPaletteQr, setShowPaletteQr] = useState(false);
  const [qrSummarizeDataCount, setQRSummarizeDataCount] = useState(10);
  const [qrSummarizeError, setQrSummarizeError] = useState<string>();

  const range = materialIdRange(info.Materials);
  const headID = range.head;
  const tailID = range.tail;

  const onPalletChange = (value: number) => {
    if (0 < value && value <= info.Materials.length) {
      setQRSummarizeDataCount(value);
      setQrSummarizeError(undefined);
    } else {
      setQrSummarizeError(`1 以上 ${info.Materials.length} 以下の整数で入力してください`);
    }
  };

  const infoCols: InfoListColDef[] = [
    { label: "ID", context: dataEntityText("issuance", info.id) },
    { label: "発行総数", context: info.Materials.length, targetUrl: new URL(materialSearchUrl(info.Materials.map((item) => item.id)), window.location.origin) },
    { label: "開始番号", context: `材料 ${headID}`, targetUrl: new URL(`/console/search-materials/${headID}/`, window.location.origin) },
    { label: "終了番号", context: `材料 ${tailID}`, targetUrl: new URL(`/console/search-materials/${tailID}/`, window.location.origin) },
    { label: "発行日", context: new Time(info.createdAt).Format("YYYY/MM/dd HH:mm:ss") },
    userInfoCol(info.User),
    { label: "備考", context: info.remarks ?? "" },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h4" fontWeight="bold">発行情報</Typography>
            <StyledButton buttonStyle="primary" onClick={onEdit} sx={{ width: 120 }}>編集</StyledButton>
          </Stack>
          <InfoList cols={infoCols} columns={3} />
        </Stack>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack gap={2} maxWidth={600} mx="auto">
            <Typography variant="h6" fontWeight="bold">QRコードを印刷</Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>パレットQR は「先頭+個数」を埋め込んだ範囲QR として印刷されます。</Alert>
            <TextField type="number" label="パレットにまとめる材料数" inputProps={{ min: 1, step: 1, max: info.Materials.length }}
              error={qrSummarizeError !== undefined} helperText={qrSummarizeError ?? ""}
              value={qrSummarizeDataCount} onChange={(e) => onPalletChange(Math.floor(Number(e.target.value) || 0))} fullWidth />
            <Stack alignItems="center" gap={1.5}>
              <StyledButton sx={{ py: 2, width: "60%" }} buttonStyle="primary" onClick={() => setShowMaterialQr(true)}>
                <Typography>材料添付用QRを印刷</Typography>
              </StyledButton>
              <StyledButton sx={{ py: 2, width: "60%" }} buttonStyle="primary" disabled={qrSummarizeError !== undefined} onClick={() => setShowPaletteQr(true)}>
                <Typography>パレット添付用QRを印刷</Typography>
              </StyledButton>
            </Stack>
          </Stack>
        </Paper>
      <QrListDialog opened={showMaterialQr} headID={headID} tailID={tailID} onClose={() => setShowMaterialQr(false)} />
      {!qrSummarizeError && <QrListDialog opened={showPaletteQr} headID={headID} tailID={tailID} paletteSummarizeCount={qrSummarizeDataCount} onClose={() => setShowPaletteQr(false)} />}
    </Stack>
  );
}
